import { button, buttonIcon, element, heading } from "@groundspark/browser-ui";
import { historyChart } from "./activity-charts";
import { breadcrumbs } from "./breadcrumbs";
import type { BranchApi } from "./branch-api";
import type { Leaflet, LeafletScans } from "./domain";

const numbers = new Intl.NumberFormat("en-GB");
const dates = new Intl.DateTimeFormat("en-GB", { dateStyle: "medium", timeZone: "UTC" });
/** Days in the "recent" total, including today. */
const RECENT_DAYS = 7;

/** How often a leaflet's QR code has been scanned, and where it leads. */
export class LeafletAnalytics {
  public readonly element = element("section", "leaflet-analytics");
  private readonly destination = element("p", "leaflet-analytics__destination");
  private readonly body = element("div", "leaflet-analytics__body");
  private request: AbortController | null = null;
  private scans: LeafletScans | null = null;
  private error: string | null = null;

  public constructor(
    private leaflet: Leaflet,
    private readonly branchId: string,
    private readonly api: BranchApi,
    onLeaflets: () => void | Promise<void>,
    onEdit: () => void | Promise<void>,
    private readonly onSettings: (leaflet: Leaflet) => void | Promise<void>,
    private readonly onFailure: (error: unknown) => boolean,
  ) {
    const edit = button("Edit leaflet", "button button--white", onEdit);
    edit.prepend(buttonIcon("edit"));
    const settings = button("Settings", "button button--white", () => onSettings(this.leaflet));
    settings.prepend(buttonIcon("settings"));
    const refresh = button("Refresh", "button button--white", () => this.load());
    refresh.prepend(buttonIcon("reload"));
    this.element.append(
      element(
        "header",
        "composer__navigation",
        breadcrumbs(leaflet.name, [{ label: "Leaflets", onSelect: onLeaflets }]),
        element("div", "composer__controls", refresh, settings, edit),
      ),
      element(
        "div",
        "panel-heading",
        heading(2, leaflet.name, "panel-heading__title"),
        element("p", "muted", "QR code scans"),
      ),
      this.destination,
      this.body,
    );
    this.update(leaflet);
  }

  /** Shows a newer state of the leaflet, such as a changed destination. */
  public update(leaflet: Leaflet): void {
    if (leaflet.id !== this.leaflet.id) return;
    this.leaflet = leaflet;
    this.destination.replaceChildren(
      "Scans open ",
      link(leaflet.qrCode.destination),
      ". Change this in ",
      button("Settings", "breadcrumbs__link", () => this.onSettings(this.leaflet)),
      ".",
    );
  }

  public async load(): Promise<void> {
    this.request?.abort();
    const request = new AbortController();
    this.request = request;
    this.error = null;
    this.render();
    try {
      const scans = await this.api.leafletScans(this.branchId, this.leaflet.id, request.signal);
      if (request.signal.aborted) return;
      this.scans = scans;
    } catch (error) {
      if (request.signal.aborted || this.onFailure(error)) return;
      this.error = error instanceof Error ? error.message : String(error);
    } finally {
      if (this.request === request) {
        this.request = null;
        this.render();
      }
    }
  }

  public destroy(): void {
    this.request?.abort();
    this.request = null;
    this.element.replaceChildren();
  }

  private render(): void {
    const content: HTMLElement[] = [];
    this.body.setAttribute("aria-busy", String(this.request !== null));
    if (this.error !== null) {
      const alert = element(
        "div",
        "alert",
        element("p", null, this.error),
        button("Retry", "button button--white", () => this.load()),
      );
      alert.setAttribute("role", "alert");
      content.push(alert);
    }
    if (this.request !== null && this.scans === null) {
      const status = element("p", "branch-loading", "Loading scans…");
      status.setAttribute("role", "status");
      content.push(status);
    }
    const scans = this.scans;
    if (scans !== null) {
      const recent = scans.history.slice(-RECENT_DAYS).reduce((sum, day) => sum + day.scans, 0);
      const first = scans.history[0];
      content.push(
        element(
          "div",
          "leaflet-analytics__summary",
          statTile("Total scans", scans.total),
          statTile(`Last ${RECENT_DAYS} days`, recent),
        ),
        historyChart(
          scans.history.map((day) => ({ date: day.date, count: day.scans })),
          {
            title:
              first === undefined
                ? "Daily scans"
                : `Daily scans · since ${dates.format(new Date(first.date))}`,
            unit: "scans",
            measure: "scans",
          },
        ),
        element(
          "p",
          "statistics-note muted",
          "Each opening of the QR code counts as a scan, including repeat scans and link previews. Nothing about the person scanning is recorded.",
        ),
      );
    }
    this.body.replaceChildren(...content);
  }
}

function statTile(label: string, value: number): HTMLElement {
  return element(
    "div",
    "stat-tile",
    element("p", "stat-tile__label", label),
    element("p", "stat-tile__value", numbers.format(value)),
  );
}

/** Destinations open in a new tab, without revealing the branch console. */
function link(address: string): HTMLAnchorElement {
  const result = element("a", null, address);
  result.href = address;
  result.target = "_blank";
  result.rel = "noopener noreferrer";
  return result;
}
